import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { usePrescriptionFlowSend } from "../context";
import { errorService } from "@ascleo/client/services/error.service";
import { KioskStep } from "../../../../../components/kiosk-step";
import {
  useCardReaderSession,
  type CardReaderReadContext,
} from "../../use-card-reader-session";
import { useAudio } from "../../../../../lib/use-audio";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useKioskPolicy } from "../../../../../lib/kiosk-policy/kiosk-policy-context";
import { KioskCardReaderPanel } from "../../scheduler/identification/KioskCardReaderPanel";
import { PrescriptionIdentificationTabs } from "./PrescriptionIdentificationTabs";

const PRESCRIPTION_CARD_TEST_IDS = {
  panel: "prescription-card-panel",
  readCard: "prescription-read-card",
  retry: "prescription-card-retry",
  readError: "prescription-card-read-error",
} as const;

/**
 * Card check step for prescription flow — reads eGK to identify patient with
 * the same identification pattern as the scheduler (KO-07): the same title,
 * mode tabs (card / name search) and card panel. Failures are shown inline
 * only (KO-01, KO-02).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PrescriptionCardCheckStep />
 * ```
 */
export function PrescriptionCardCheckStep() {
  const { t } = useTranslation();
  useAudio("prescription-cardCheck");
  const send = usePrescriptionFlowSend();
  const kioskPolicy = useKioskPolicy();
  const manualFallbackAvailable =
    kioskPolicy.source === "current" &&
    kioskPolicy.manualPrescriptionFallbackEnabled;

  const handleReadCard = useCallback(
    async ({ readCard, setPhase, setErrorMessage }: CardReaderReadContext) => {
      const result = await readCard();
      if (result.status === "MERGED") {
        await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("error");
        return;
      }

      const status = result.status.toUpperCase();
      const crlogId = result.crlogId;
      const cardData = result.cardData;

      // Map card insurance type to flow insurance type
      const insuranceType =
        cardData?.insurance?.insuranceType === "private"
          ? ("PRIVATE" as const)
          : ("PUBLIC" as const);

      switch (status) {
        case "APPLIED":
        {
          const explicitPatientId = result.patientId;
          if (!explicitPatientId) {
            errorService.report(
              new Error("Card read completed without a patientId"),
              {
                layer: "rpc",
                source: "frontdesk.prescription.card-read",
                suppressToast: true,
              },
            );
            setErrorMessage(t("self-service.check-in.card-check.read-error"));
            setPhase("card-detected");
            return;
          }
          if (!crlogId?.trim()) {
            errorService.report(
              new Error("Card read completed without a crlogId"),
              {
                layer: "rpc",
                source: "frontdesk.prescription.card-read",
                suppressToast: true,
              },
            );
            setErrorMessage(t("self-service.check-in.card-check.read-error"));
            setPhase("card-detected");
            return;
          }
          send({
            type: "CARD_OK",
            patientId: explicitPatientId,
            crlogId,
            insuranceType,
          });
          return;
        }
        case "MATCHING": {
          await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
          send({ type: "NO_PATIENT_FOUND" });
          return;
        }
        case "CANCELLED":
          setErrorMessage(t("self-service.check-in.card-check.error"));
          setPhase("card-detected");
          return;
        default:
          send({ type: "CARD_MISSING" });
          return;
      }
    },
    [send, t],
  );

  const handleReadCardError = useCallback(
    (error: unknown) => {
      errorService.report(error, {
        layer: "rpc",
        source: "frontdesk.prescription.card-read",
        suppressToast: true,
      });
    },
    [],
  );

  const cardReader = useCardReaderSession({
    enabled: true,
    settingsErrorMessage: t("self-service.check-in.card-check.settings-error"),
    unreadableCardMessage: t(
      manualFallbackAvailable
        ? "self-service.identification.card.help-with-search"
        : "self-service.identification.card.help-reception",
    ),
    readErrorMessage: t("self-service.check-in.card-check.read-error"),
    onReadCard: handleReadCard,
    onReadError: handleReadCardError,
  });

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.identification.title")}
      lead={t(
        manualFallbackAvailable
          ? "self-service.identification.lead"
          : "self-service.identification.lead-card-only",
      )}
    >
      {manualFallbackAvailable ? (
        <PrescriptionIdentificationTabs
          activeMode="card"
          onSearch={() => send({ type: "MANUAL_LOOKUP" })}
          onCard={() => undefined}
          searchDisabled={cardReader.phase === "reading"}
        />
      ) : null}
      <KioskCardReaderPanel
        cardReader={cardReader}
        testIds={PRESCRIPTION_CARD_TEST_IDS}
        searchAction={
          manualFallbackAvailable
            ? {
                label: t("self-service.identification.card.without-card"),
                icon: Search,
                onClick: () => send({ type: "MANUAL_LOOKUP" }),
                testId: "frontdesk-prescription-card-name-lookup-btn",
                disabled: cardReader.phase === "reading",
              }
            : undefined
        }
      />
    </KioskStep>
  );
}
